import CheckList from "@/domain/model/checklist/CheckList.js";
import CheckListItem from "@/domain/model/checklist/item/CheckListItem.js";
import CheckListItemMap from "@/domain/model/checklist/item/CheckListItemMap.js";
import CheckListItemOption from "@/domain/model/checklist/item/option/CheckListItemOption.js";
import CheckListItemOptionMap from "@/domain/model/checklist/item/option/CheckListItemOptionMap.js";
import Note from "@/domain/model/schedule/question/option/note/Note.js";
import NoteMap from "@/domain/model/schedule/question/option/note/NoteMap.js";

/**
 * A checklist as it stood when an inspection was answered against it: the frozen copy
 * the server keeps on a CheckListVersion. It hands back ordinary CheckListItem and
 * CheckListItemOption objects built from the copy, so the screens that draw a live
 * checklist can draw the frozen one unchanged - but nothing in them reaches back to
 * the live records, which may since have been renamed, re-ordered or removed.
 *
 * The item and option ids are the live record ids, so they match the ids the answers
 * point at.
 */
export default class CheckListSnapshot {

  // The newest copy layout this client can read. A newer one is left unread and the
  // caller falls back to the live checklist, which is what it showed before copies.
  static SUPPORTED_FORMAT = 1;

  // Parsing the same copy for every row of every redraw is wasted work, and a copy
  // never changes once written, so the parsed form is kept against its text.
  static _cache = new Map();
  static _CACHE_LIMIT = 200;

  constructor(domain, data) {
    this.domain = domain;
    this.data = data;
  }

  /**
   * The copy in the given text, or null when there is none, it does not parse, or it
   * is in a layout this client does not know.
   */
  static parse(domain, text) {
    if (typeof text !== 'string' || text.length === 0) {
      return null;
    }
    let data = CheckListSnapshot._cache.get(text);
    if (data === undefined) {
      data = CheckListSnapshot._read(text);
      if (CheckListSnapshot._cache.size >= CheckListSnapshot._CACHE_LIMIT) {
        CheckListSnapshot._cache.clear();
      }
      CheckListSnapshot._cache.set(text, data);
    }
    if (data == null) {
      return null;
    }
    return new CheckListSnapshot(domain, data);
  }

  static _read(text) {
    let data = null;
    try {
      data = JSON.parse(text);
    } catch (e) {
      return null;
    }
    if (data == null || typeof data !== 'object' || Array.isArray(data)) {
      return null;
    }
    const format = data.format;
    if (typeof format !== 'number' || format < 1 || format > CheckListSnapshot.SUPPORTED_FORMAT) {
      return null;
    }
    if (!Array.isArray(data.items)) {
      return null;
    }
    return data;
  }

  format() {
    return this.data.format;
  }

  name() {
    return CheckListSnapshot._string(this.data.name);
  }

  description() {
    return CheckListSnapshot._string(this.data.description);
  }

  hasDescription() {
    return this.description().length > 0;
  }

  // The checklist-level fields a built-in schedule carries. Each is written only when
  // set, so a copy taken before they existed - or of an ordinary checklist - reads empty.

  kind() {
    return CheckListSnapshot._string(this.data.kind);
  }

  isBuiltIn() {
    return this.kind() === CheckList.Values.Kind.BuiltIn;
  }

  declaration() {
    return CheckListSnapshot._string(this.data.declaration);
  }

  hasDeclaration() {
    return this.declaration().length > 0;
  }

  scheduleNumber() {
    return CheckListSnapshot._string(this.data.scheduleNumber);
  }

  printLayout() {
    return CheckListSnapshot._string(this.data.printLayout);
  }

  /**
   * The questions and information blocks in the order the copy holds them, which the
   * server writes sorted by index and then id.
   */
  items() {
    return this.data.items
      .filter(entry => entry != null && typeof entry === 'object')
      .map(entry => CheckListSnapshot.toItem(this.domain, entry));
  }

  /** The item with the given live id, or null when the copy has no such item. */
  item(itemId) {
    const entry = this._itemEntry(itemId);
    return entry ? CheckListSnapshot.toItem(this.domain, entry) : null;
  }

  hasItem(itemId) {
    return this._itemEntry(itemId) != null;
  }

  /** The item's choices in the copy's order, or an empty list for an unknown item. */
  options(itemId) {
    const entry = this._itemEntry(itemId);
    if (!entry) {
      return [];
    }
    return CheckListSnapshot._optionEntries(entry)
      .map(option => CheckListSnapshot.toOption(this.domain, option));
  }

  /** The choice with the given live id on the given item, or null. */
  option(itemId, optionId) {
    const entry = this._itemEntry(itemId);
    if (!entry) {
      return null;
    }
    const found = CheckListSnapshot._optionEntries(entry).find(option => option.id === optionId);
    return found ? CheckListSnapshot.toOption(this.domain, found) : null;
  }

  // A Rollup's nested questions sit under its "items" in the copy, not in the top-level
  // list, but they are looked up by id the same way - so an answer to a nested question
  // reads its question and choices from the copy and is not taken for one the copy lacks.
  _itemEntry(itemId) {
    const top = this.data.items.find(entry => entry != null && entry.id === itemId);
    if (top) {
      return top;
    }
    for (const entry of this.data.items) {
      const nested = CheckListSnapshot._nestedEntries(entry).find(child => child.id === itemId);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  static _nestedEntries(entry) {
    if (entry == null || !Array.isArray(entry.items)) {
      return [];
    }
    return entry.items.filter(child => child != null && typeof child === 'object' && !Array.isArray(child));
  }

  static _optionEntries(entry) {
    if (!Array.isArray(entry.options)) {
      return [];
    }
    return entry.options.filter(option => option != null && typeof option === 'object');
  }

  static _string(value) {
    if (value == null) {
      return "";
    }
    return String(value);
  }

  /** A CheckListItem carrying the copy's fields, with its choices attached. */
  static toItem(domain, entry) {
    const options = new CheckListItemOptionMap(domain, {});
    CheckListSnapshot._optionEntries(entry).forEach(option => {
      options.add(CheckListSnapshot.toOption(domain, option));
    });
    const data = {
      '@rid': entry.id,
      index: entry.index,
      name: entry.name,
      type: entry.type,
      content: entry.content,
      multiSelect: entry.multiSelect,
      warnOnChange: entry.warnOnChange,
      warnChangeThreshold: entry.warnChangeThreshold,
    };
    // Copied only when the copy holds them, so a copy written before they existed
    // gives an item that reads exactly as it did then.
    CheckListSnapshot._copyPresent(entry, data, ['section', 'vehicleTypes', 'showParts', 'autoSetCurrentUser', 'preventChanges',
      'autoSetCurrentVehicle', 'vehicleKinds']);
    // The copy holds the default employees and vehicles as arrays of ids, written only when there are any.
    if (Array.isArray(entry.defaultEmployees)) {
      data.DefaultEmployeeMap = entry.defaultEmployees;
    }
    if (Array.isArray(entry.defaultVehicles)) {
      data.DefaultVehicleMap = entry.defaultVehicles;
    }
    const item = new CheckListItem(domain, data);
    item.withOptions(options);
    // A Rollup carries "rollup": true and its nested questions under "items", both
    // written only when set. A nested question never has either of its own.
    if (entry.rollup === true) {
      item.withRollup(true);
      const nested = new CheckListItemMap(domain, {});
      CheckListSnapshot._nestedEntries(entry).forEach(child => {
        const childEntry = { ...child };
        delete childEntry.rollup;
        delete childEntry.items;
        nested.add(CheckListSnapshot.toItem(domain, childEntry));
      });
      item.withRollupItems(nested);
    }
    CheckListSnapshot._copyNotes(domain, entry, item);
    return item;
  }

  /** A CheckListItemOption carrying the copy's name, place, severity, meaning, kind, letter and notes. */
  static toOption(domain, entry) {
    const data = {
      '@rid': entry.id,
      index: entry.index,
      name: entry.name,
      category: entry.category,
    };
    CheckListSnapshot._copyPresent(entry, data, ['meaning', 'kind', 'position']);
    const option = new CheckListItemOption(domain, data);
    CheckListSnapshot._copyNotes(domain, entry, option);
    return option;
  }

  /**
   * The copy's notes as an in-memory NoteMap on the item or option, for display. The
   * server writes them inline as "notes": [{position, value}, ...] - text, not record ids,
   * so an old version keeps the notes it had - and only when there is at least one. Entries
   * that are not objects are skipped. The notes get ids made from the owner's id and their
   * place in the copy, so the same copy always reads the same. Without notes in the copy
   * the key is not set at all.
   */
  static _copyNotes(domain, entry, record) {
    if (!Array.isArray(entry.notes)) {
      return;
    }
    const map = new NoteMap(domain, {});
    entry.notes.forEach((note, place) => {
      if (note == null || typeof note !== 'object' || Array.isArray(note)) {
        return;
      }
      map.add(new Note(domain, {
        '@rid': `${CheckListSnapshot._string(entry.id)}/note/${place}`,
        position: CheckListSnapshot._string(note.position),
        value: CheckListSnapshot._string(note.value),
      }));
    });
    if (map.size() > 0) {
      record.withNoteMap(map);
    }
  }

  static _copyPresent(entry, data, keys) {
    keys.forEach(key => {
      if (entry[key] != null) {
        data[key] = entry[key];
      }
    });
  }
}
